import api from './api.service';
import axios from 'axios';
import { API_BASE_URL } from '@/config/apiBaseUrl';
import type { RejectionPayload } from '@/services/rejection.service';

const base = '/estimations/assembly-estimates';

function authHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const assemblyEstimateService = {
  list: (search?: string) => api.get(base, { params: { search } }),
  get: (id: string) => api.get(`${base}/${id}`),
  boundaries: () => api.get(`${base}/boundaries`),
  create: (body: Record<string, unknown>) => api.post(base, body),
  update: (id: string, body: Record<string, unknown>) => api.put(`${base}/${id}`, body),
  remove: (id: string) => api.delete(`${base}/${id}`),
  addNode: (id: string, body: Record<string, unknown>) => api.post(`${base}/${id}/nodes`, body),
  updateNode: (id: string, nodeId: string, body: Record<string, unknown>) => api.put(`${base}/${id}/nodes/${nodeId}`, body),
  reorderNode: (id: string, nodeId: string, direction: 'up' | 'down') =>
    api.post(`${base}/${id}/nodes/${nodeId}/reorder`, { direction }),
  deleteNode: (id: string, nodeId: string) => api.delete(`${base}/${id}/nodes/${nodeId}`),
  copyNode: (id: string, nodeId: string) => api.post(`${base}/${id}/nodes/${nodeId}/copy`, {}),
  moveResources: (id: string, fromNodeId: string, toNodeId: string) =>
    api.post(`${base}/${id}/move-resources`, { from_node_id: fromNodeId, to_node_id: toNodeId }),
  addMaterial: (id: string, nodeId: string, body: Record<string, unknown>) =>
    api.post(`${base}/${id}/nodes/${nodeId}/materials`, body),
  updateMaterial: (id: string, materialId: string, body: Record<string, unknown>) =>
    api.put(`${base}/${id}/materials/${materialId}`, body),
  deleteMaterial: (id: string, materialId: string) => api.delete(`${base}/${id}/materials/${materialId}`),
  addOperation: (id: string, nodeId: string, body: Record<string, unknown>) =>
    api.post(`${base}/${id}/nodes/${nodeId}/operations`, body),
  updateOperation: (id: string, operationId: string, body: Record<string, unknown>) =>
    api.put(`${base}/${id}/operations/${operationId}`, body),
  deleteOperation: (id: string, operationId: string) => api.delete(`${base}/${id}/operations/${operationId}`),
  saveIndirect: (id: string, body: Record<string, unknown>, indirectId?: string) =>
    indirectId ? api.put(`${base}/${id}/indirects/${indirectId}`, body) : api.post(`${base}/${id}/indirects`, body),
  deleteIndirect: (id: string, indirectId: string) => api.delete(`${base}/${id}/indirects/${indirectId}`),
  saveEscalation: (id: string, body: Record<string, unknown>, escalationId?: string) =>
    escalationId
      ? api.put(`${base}/${id}/escalations/${escalationId}`, body)
      : api.post(`${base}/${id}/escalations`, body),
  deleteEscalation: (id: string, escalationId: string) => api.delete(`${base}/${id}/escalations/${escalationId}`),
  setNodeActuals: (id: string, nodeId: string, body: Record<string, unknown>) =>
    api.put(`${base}/${id}/nodes/${nodeId}/actuals`, body),
  customerSummary: (id: string) => api.get(`${base}/${id}/customer-summary`),
  saveCustomerSummary: (id: string, body?: { document_id?: string }) =>
    api.post(`${base}/${id}/customer-summary`, body || {}),
  saveStatement: (id: string, body: Record<string, unknown>) => api.post(`${base}/${id}/statements`, body),
  deleteStatement: (id: string, statementId: string) => api.delete(`${base}/${id}/statements/${statementId}`),
  seedBom: (id: string, bomId: string, quantity: number) => api.post(`${base}/${id}/seed-bom`, { bom_id: bomId, quantity }),
  copy: (id: string) => api.post(`${base}/${id}/copy`, {}),
  submit: (id: string) => api.post(`${base}/${id}/submit`, {}),
  approve: (id: string, remarks?: string) => api.post(`${base}/${id}/approve`, { remarks }),
  reject: (id: string, payload: RejectionPayload | string) =>
    api.post(
      `${base}/${id}/reject`,
      typeof payload === 'string'
        ? { rejection_reason_code: 'OTHER', remarks: payload }
        : payload
    ),
  revise: (id: string) => api.post(`${base}/${id}/revise`, {}),
  convert: (id: string, nodeIds?: string[]) => api.post(`${base}/${id}/convert`, { node_ids: nodeIds }),
  compare: (left: string, right: string) => api.get(`${base}/compare`, { params: { left, right } }),
  templates: () => api.get(`${base}/templates`),
  saveNodeTemplate: (id: string, nodeId: string, templateName: string) =>
    api.post(`${base}/${id}/nodes/${nodeId}/template`, { template_name: templateName }),
  setTemplateStatus: (templateId: string, status: string) =>
    api.put(`${base}/templates/${templateId}/status`, { status }),
  applyTemplate: (id: string, templateId: string) => api.post(`${base}/${id}/apply-template`, { template_id: templateId }),
  rateCards: () => api.get(`${base}/rate-cards`),
  saveRateCard: (body: Record<string, unknown>, id?: string) =>
    id ? api.put(`${base}/rate-cards/${id}`, body) : api.post(`${base}/rate-cards`, body),
  addOffer: (id: string, body: Record<string, unknown>) => api.post(`${base}/${id}/offers`, body),
  updateOffer: (id: string, offerId: string, body: Record<string, unknown>) =>
    api.put(`${base}/${id}/offers/${offerId}`, body),
  deleteOffer: (id: string, offerId: string) => api.delete(`${base}/${id}/offers/${offerId}`),
  selectOffer: (id: string, offerId: string) => api.post(`${base}/${id}/offers/${offerId}/select`, {}),
  downloadImportTemplate: async () => {
    const response = await axios.get(`${API_BASE_URL}${base}/excel/template`, {
      responseType: 'blob',
      headers: authHeader(),
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'assembly-estimate-import-template.xlsx';
    link.click();
    URL.revokeObjectURL(url);
  },
  downloadExport: async (id: string, filenameHint?: string) => {
    const response = await axios.get(`${API_BASE_URL}${base}/${id}/export`, {
      responseType: 'blob',
      headers: authHeader(),
    });
    const disposition = String(response.headers['content-disposition'] || '');
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `${filenameHint || 'assembly-estimate'}.xlsx`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },
  previewImport: async (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axios.post(`${API_BASE_URL}${base}/${id}/import/preview`, form, { headers: authHeader() });
    return response.data;
  },
  confirmImport: async (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axios.post(`${API_BASE_URL}${base}/${id}/import`, form, { headers: authHeader() });
    return response.data;
  },
};
